export type TxnSource = 'apple-card' | 'bank';
export type AccountKind = 'credit' | 'checking';
export type RuleMatch = 'contains' | 'startsWith' | 'exact' | 'regex';

export interface Account {
  id: string; name: string; kind: AccountKind; source: TxnSource;
  mask: string; createdAt: number;
}

export interface Group { id: string; name: string; color: string; order: number; isSpending: boolean; }
export interface Category { id: string; name: string; groupId: string; order: number; }

export interface Rule {
  id: string; match: RuleMatch; pattern: string;
  categoryId: string | null; isTransfer: boolean;
  priority: number; hitCount: number; createdAt: number;
}

export interface Budget {
  /** Default monthly limit in cents. */
  amount: number;
  /** Per-month limits keyed 'YYYY-MM', overriding `amount` for that month. */
  overrides: Record<string, number>;
}

export type RecurringStatus = 'confirmed' | 'dismissed' | 'pinned';

export interface RecurringState {
  status: RecurringStatus;
  updatedAt: number;
}

/** Stored transaction. `amount` is SIGNED INTEGER CENTS; negative = money out. */
export interface Txn {
  id: string; date: string; amount: number;
  description: string; merchant: string; matchKey: string;
  accountId: string; categoryId: string | null;
  isTransfer: boolean; excluded: boolean;
  source: TxnSource; importId: string; fingerprint: string;
  note: string; purchasedBy: string; sourceCategory: string;
}

/** Adapter output — no identity, no category yet. */
export interface ParsedTxn {
  date: string; amount: number;
  description: string; merchant: string; matchKey: string;
  accountId: string; source: TxnSource;
  isTransfer: boolean; purchasedBy: string; sourceCategory: string;
}

export interface FingerprintedTxn extends ParsedTxn { fingerprint: string; }

/** Ready for the preview table. */
export interface StagedTxn extends FingerprintedTxn {
  categoryId: string | null;
  matchedRuleId: string | null;
  isDuplicate: boolean;
}

export interface BalancePoint { accountId: string; date: string; balance: number; }

/** RTDB shape at `balances/{accountId}/{isoDate}` — a closing balance in cents. */
export type BalanceIndex = Record<string, Record<string, number>>;

export interface AdapterResult {
  source: TxnSource;
  txns: ParsedTxn[];
  balances: BalancePoint[];
  pendingCount: number;
  accountMask: string;
  accountKind: AccountKind;
}

export interface ImportRecord {
  id: string; filename: string; source: TxnSource; accountId: string;
  rowCount: number; importedCount: number; duplicateCount: number;
  pendingCount: number; dateFrom: string; dateTo: string;
  txnIds: string[]; createdAt: number;
}

export interface SavingsGoal {
  id: string; name: string; targetCents: number; monthlyCents: number; createdAt: number;
}

/** Rate as integer basis points (700 = 7%) so no float is ever written to RTDB. */
export interface Projection {
  startCents: number; monthlyCents: number; rateBasisPoints: number; years: number;
}

export const DEFAULT_PROJECTION: Projection = {
  startCents: 0, monthlyCents: 0, rateBasisPoints: 700, years: 20,
};

/**
 * Slider bounds. Wide enough to cover the range of returns worth exploring,
 * bounded so the tool stays something arithmetic can be honest about rather
 * than extrapolating to rates nobody should plan around.
 */
export const RATE_MIN_BP = 0;
export const RATE_MAX_BP = 1500;
export const RATE_STEP_BP = 25;
export const YEARS_MIN = 1;
export const YEARS_MAX = 40;

/** Rate as integer basis points (650 = 6.5%); money as integer cents. */
export interface MortgageSettings {
  /** Balance owed today. */
  principalCents: number;
  rateBasisPoints: number;
  /** Months remaining on the loan. */
  termMonths: number;
  extraMonthlyCents: number;
}

export const DEFAULT_MORTGAGE: MortgageSettings = {
  principalCents: 0, rateBasisPoints: 650, termMonths: 360, extraMonthlyCents: 0,
};

export interface FireSettings {
  /**
   * Invested assets today. Deliberately separate from `Projection.startCents`:
   * a savings goal is a bank balance and a FIRE portfolio is market money, and
   * sharing the field would let one screen silently edit the other.
   */
  portfolioCents: number;
  monthlyContribCents: number;
  /** Expected REAL (inflation-adjusted) return. Every date on the screen is
   *  therefore in today's dollars and needs no inflation term anywhere. */
  rateBasisPoints: number;
  /** Safe withdrawal rate; 400 = 4%. */
  swrBasisPoints: number;
  spendSource: 'ledger' | 'manual';
  manualAnnualSpendCents: number;
  /** Subtract 12 x derived P&I from perpetual spend, because the loan ends. */
  deductMortgagePI: boolean;
  currentAge: number;
  coastAge: number;
}

export const DEFAULT_FIRE: FireSettings = {
  portfolioCents: 0, monthlyContribCents: 0, rateBasisPoints: 500,
  swrBasisPoints: 400, spendSource: 'ledger', manualAnnualSpendCents: 0,
  deductMortgagePI: true, currentAge: 35, coastAge: 65,
};

/** A zero withdrawal rate is an infinite target; the upper bound keeps the
 *  tool to rates arithmetic can be honest about. */
export const SWR_MIN_BP = 100;
export const SWR_MAX_BP = 1000;
export const SWR_STEP_BP = 25;

/** 60 years. Both the amortization walk and the FIRE simulation stop here
 *  rather than looping on a loan or a portfolio that never converges. */
export const SIM_MAX_MONTHS = 720;

export const moneyBase = (uid: string): string => `users/${uid}/money`;

/** The real bank export is 1,510 records — ~3,000 paths in one write is enough to time out. */
export const COMMIT_CHUNK_SIZE = 500;

/**
 * Default import window. The bank export runs 5.4 years, which is more history
 * than wanted; at 24 months it yields 683 kept and 827 skipped. Editable on the
 * import screen — this is a default, not a hardcoded filter.
 */
export const DEFAULT_CUTOFF_MONTHS = 24;

/** ISO date DEFAULT_CUTOFF_MONTHS before `from`. Injected for testability. */
export function defaultCutoffDate(from: Date): string {
  const d = new Date(from.getFullYear(), from.getMonth() - DEFAULT_CUTOFF_MONTHS, from.getDate());
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/** House palette, from hours-tracker.component.scss. */
export const GROUP_COLORS = {
  sage: '#7aab8a', dustyRed: '#c4786a', gold: '#c9a84c',
  dustyBlue: '#6b9db0', warmMuted: '#8a7060', ink: '#3a2a1e',
};

export const SEED_GROUPS: Group[] = [
  { id: 'g-essentials', name: 'Essentials', color: GROUP_COLORS.sage,       order: 0, isSpending: true },
  { id: 'g-lifestyle',  name: 'Lifestyle',  color: GROUP_COLORS.dustyRed,   order: 1, isSpending: true },
  { id: 'g-transport',  name: 'Transport',  color: GROUP_COLORS.gold,       order: 2, isSpending: true },
  { id: 'g-health',     name: 'Health',     color: GROUP_COLORS.dustyBlue,  order: 3, isSpending: true },
  { id: 'g-other',      name: 'Other',      color: GROUP_COLORS.warmMuted,  order: 4, isSpending: true },
  { id: 'g-income',     name: 'Income',     color: GROUP_COLORS.ink,        order: 5, isSpending: false },
];

const cat = (id: string, name: string, groupId: string, order: number): Category =>
  ({ id, name, groupId, order });

export const SEED_CATEGORIES: Category[] = [
  cat('c-groceries',  'Groceries',        'g-essentials', 0),
  cat('c-rent',       'Rent/Mortgage',    'g-essentials', 1),
  cat('c-utilities',  'Utilities',        'g-essentials', 2),
  cat('c-insurance',  'Insurance',        'g-essentials', 3),
  cat('c-connect',    'Phone & Internet', 'g-essentials', 4),
  cat('c-restaurants','Restaurants',      'g-lifestyle',  0),
  cat('c-shopping',   'Shopping',         'g-lifestyle',  1),
  cat('c-entertain',  'Entertainment',    'g-lifestyle',  2),
  cat('c-travel',     'Travel',           'g-lifestyle',  3),
  cat('c-subs',       'Subscriptions',    'g-lifestyle',  4),
  cat('c-gas',        'Gas',              'g-transport',  0),
  cat('c-carmaint',   'Car Maintenance',  'g-transport',  1),
  cat('c-parking',    'Parking & Tolls',  'g-transport',  2),
  cat('c-rideshare',  'Rideshare',        'g-transport',  3),
  cat('c-medical',    'Medical',          'g-health',     0),
  cat('c-pharmacy',   'Pharmacy',         'g-health',     1),
  cat('c-fitness',    'Fitness',          'g-health',     2),
  cat('c-gifts',      'Gifts',            'g-other',      0),
  cat('c-fees',       'Fees',             'g-other',      1),
  cat('c-misc',       'Misc',             'g-other',      2),
  cat('c-paycheck',   'Paycheck',         'g-income',     0),
  cat('c-interest',   'Interest',         'g-income',     1),
  cat('c-refunds',    'Refunds',          'g-income',     2),
];

/**
 * Transfer rules only. These affect CORRECTNESS, not convenience: 806 of 1,510
 * real bank rows are internal money movement, and a missed transfer inflates
 * reported spending. Patterns are copied from the real exports, not guessed —
 * the bank writes "APPLECARD" with no space.
 *
 * VENMO (130 real rows) is deliberately absent. It can be reimbursement or real
 * spending, so those land in the review queue for a human decision.
 *
 * ATM rows are similarly left for review. Real matches are "ATM Withdraw Fee"
 * (a fee, which is spending, not a transfer) and "Withdrawal at ATM #…" (cash
 * withdrawal, ambiguous like Venmo).
 */
export const SEED_TRANSFER_RULES: Rule[] = [
  'APPLECARD',
  'HOME BANKING TRANSFER',
  'TRANSFER TO SHARE',
  'TRANSFER FROM SHARE',
  'TRANSFER FROM LOAN',
  'FUNDS TRANSFER',
].map((pattern, i) => ({
  id: `r-seed-${i}`, match: 'contains' as RuleMatch, pattern,
  categoryId: null, isTransfer: true, priority: i, hitCount: 0, createdAt: 0,
}));
